import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LogOut, ChevronDown, UserRound } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function Topbar() {
  const { t } = useTranslation("common");
  const { user, tenant, signOut } = useAuth();

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-6 backdrop-blur-md"
    >
      <div className="flex flex-col">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Workspace
        </span>
        <span data-testid="topbar-tenant" className="font-display text-sm font-semibold text-slate-900">
          {tenant?.name}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <LanguageSwitcher />

        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-50 text-xs font-semibold text-indigo-700">
              {initials(user?.name)}
            </div>
            <div className="hidden flex-col items-start leading-tight md:flex">
              <span className="font-medium text-slate-900">{user?.name}</span>
              <span className="text-[11px] text-slate-500">{user?.email}</span>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-testid="user-menu">
            <DropdownMenuLabel className="flex items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">{user?.name}</span>
                <span className="text-xs font-normal text-slate-500">{user?.email}</span>
              </div>
              <RoleBadge role={user?.role} />
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild data-testid="user-menu-profile">
              <Link to="/settings/profile" className="cursor-pointer text-slate-700">
                <UserRound className="mr-2 h-4 w-4" strokeWidth={1.75} />
                {t("myProfile")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={signOut}
              data-testid="sign-out-btn"
              className="cursor-pointer text-slate-700 focus:bg-red-50 focus:text-red-700"
            >
              <LogOut className="mr-2 h-4 w-4" strokeWidth={1.75} />
              {t("signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
